import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

export interface CartItem {
  id: number;
  produto_id: number;
  quantidade: number;
  preco_unitario: number;
  observacoes?: string;
  personalizacoes: any[];
  produto_nome: string;
  imagem_url?: string;
  descricao?: string;
}

export interface Cart {
  id: number;
  status: string;
  observacoes?: string;
  data_criacao: string;
  data_expiracao: string;
}

export interface EnderecoEntrega {
  rua: string;
  bairro: string;
  numero_casa: string;
  cep: string;
  cidade?: string;
  estado?: string;
  complemento?: string;
}

export interface CartData {
  carrinho: Cart | null;
  itens: CartItem[];
  total: number;
  endereco_entrega: EnderecoEntrega | null;
  entregaGratis: boolean;
}

// Helpers para Carrinho de Visitante (localStorage)
const getGuestStorageKey = (empresaId: number) => `delivery_guest_cart_${empresaId}`;

const loadGuestCartFromStorage = (empresaId: number): CartItem[] => {
  try {
    const raw = localStorage.getItem(getGuestStorageKey(empresaId));
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('Erro ao ler carrinho do localStorage:', e);
    return [];
  }
};

const saveGuestCartToStorage = (empresaId: number, itens: CartItem[]) => {
  try {
    localStorage.setItem(getGuestStorageKey(empresaId), JSON.stringify(itens));
  } catch (e) {
    console.error('Erro ao salvar carrinho no localStorage:', e);
  }
};

const calculateGuestTotal = (itens: CartItem[]): number => {
  return itens.reduce((sum, item) => {
    const personalizacoesTotal = (item.personalizacoes || []).reduce(
      (acc: number, p: any) => acc + (Number(p.preco_adicional || 0) * Number(p.quantidade || 1)),
      0
    );
    return sum + (Number(item.preco_unitario || 0) + personalizacoesTotal) * item.quantidade;
  }, 0);
};

export const useCart = (empresaId: number) => {
  const { usuario, token } = useAuth();
  const [cartData, setCartData] = useState<CartData>(() => {
    // Inicialização segura para visitantes
    const guestItems = typeof window !== 'undefined' ? loadGuestCartFromStorage(empresaId) : [];
    return {
      carrinho: null,
      itens: guestItems,
      total: calculateGuestTotal(guestItems),
      endereco_entrega: null,
      entregaGratis: false
    };
  });

  const usuarioId = usuario?.id;
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const sincronizandoRef = useRef(false);
  const lastSyncedAuthRef = useRef<string>('');

  // Função para verificar entrega grátis
  const verificarEntregaGratis = useCallback(async () => {
    if (!usuarioId || !token) {
      return;
    }

    try {
      const response = await api.get(`/api/carrinho/entrega-gratis/${usuarioId}`);
      const temEntregaGratis = !!response.data?.temDireitoEntregaGratis;

      setCartData(prev => {
        if (prev.entregaGratis === temEntregaGratis) return prev;
        return {
          ...prev,
          entregaGratis: temEntregaGratis
        };
      });
    } catch (error: any) {
      console.error('❌ Erro ao verificar entrega grátis:', error);
      setCartData(prev => {
        if (!prev.entregaGratis) return prev;
        return {
          ...prev,
          entregaGratis: false
        };
      });
    }
  }, [usuarioId, token]);

  // Função para formatar endereço
  const formatarEndereco = useCallback((endereco: EnderecoEntrega) => {
    if (!endereco) return '';
    const cidade = endereco.cidade ? `, ${endereco.cidade}` : '';
    return `${endereco.rua}, ${endereco.numero_casa} - ${endereco.bairro}${cidade}, CEP: ${endereco.cep}`;
  }, []);

  // Carregar carrinho do servidor
  const loadCart = useCallback(async () => {
    if (!usuarioId || !token) {
      // Se visitante, ler do localStorage
      const guestItems = loadGuestCartFromStorage(empresaId);
      setCartData({
        carrinho: null,
        itens: guestItems,
        total: calculateGuestTotal(guestItems),
        endereco_entrega: null,
        entregaGratis: false
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get(`/api/carrinho/${empresaId}`);
      setCartData(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setCartData({
          carrinho: null,
          itens: [],
          total: 0,
          endereco_entrega: null,
          entregaGratis: false
        });
      } else {
        console.error('Erro ao carregar carrinho:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [usuarioId, token, empresaId]);

  // Adicionar item ao carrinho (suporta tanto visitante quanto usuário logado)
  const addToCart = useCallback(async (
    produtoId: number, 
    quantidade: number = 1, 
    observacoes?: string, 
    personalizacoes?: any[],
    produtoInfo?: { nome?: string; preco?: number; imagem_url?: string; descricao?: string }
  ) => {
    if (isUpdating) {
      return false;
    }

    setIsUpdating(true);

    try {
      // ── MODO VISITANTE (GUEST) ───────────────────────────────────
      if (!usuario || !token) {
        const itensAtuais = loadGuestCartFromStorage(empresaId);

        // Busca se existe item idêntico (mesmo produto, observação e personalizações)
        const itemIndex = itensAtuais.findIndex(item => 
          item.produto_id === produtoId &&
          (item.observacoes || '') === (observacoes || '') &&
          JSON.stringify(item.personalizacoes || []) === JSON.stringify(personalizacoes || [])
        );

        let novosItens: CartItem[];
        if (itemIndex >= 0) {
          novosItens = [...itensAtuais];
          novosItens[itemIndex] = {
            ...novosItens[itemIndex],
            quantidade: novosItens[itemIndex].quantidade + quantidade
          };
        } else {
          const novoItem: CartItem = {
            id: Date.now() + Math.floor(Math.random() * 1000),
            produto_id: produtoId,
            quantidade,
            preco_unitario: produtoInfo?.preco || 0,
            observacoes: observacoes || '',
            personalizacoes: personalizacoes || [],
            produto_nome: produtoInfo?.nome || 'Item',
            imagem_url: produtoInfo?.imagem_url,
            descricao: produtoInfo?.descricao
          };
          novosItens = [...itensAtuais, novoItem];
        }

        saveGuestCartToStorage(empresaId, novosItens);
        setCartData(prev => ({
          ...prev,
          itens: novosItens,
          total: calculateGuestTotal(novosItens)
        }));

        toast.success('Item adicionado ao carrinho!', { duration: 1500 });
        return true;
      }

      // ── MODO AUTENTICADO (API / MYSQL) ───────────────────────────
      const payload = {
        produto_id: produtoId,
        quantidade,
        observacoes,
        personalizacoes
      };
      
      await api.post(`/api/carrinho/${empresaId}/adicionar`, payload);
      toast.success('Item adicionado ao carrinho!', { duration: 1500 });
      
      setTimeout(() => loadCart(), 100);
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.erro || 'Erro ao adicionar item';
      toast.error(errorMessage, { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuario, token, empresaId, loadCart, isUpdating]);

  // Atualizar quantidade de um item
  const updateItemQuantity = useCallback(async (itemId: number, quantidade: number) => {
    setIsUpdating(true);
    try {
      // ── MODO VISITANTE ──────────────────────────────────────────
      if (!usuario || !token) {
        const itensAtuais = loadGuestCartFromStorage(empresaId);
        let novosItens: CartItem[];

        if (quantidade <= 0) {
          novosItens = itensAtuais.filter(it => it.id !== itemId);
        } else {
          novosItens = itensAtuais.map(it => it.id === itemId ? { ...it, quantidade } : it);
        }

        saveGuestCartToStorage(empresaId, novosItens);
        setCartData(prev => ({
          ...prev,
          itens: novosItens,
          total: calculateGuestTotal(novosItens)
        }));
        return true;
      }

      // ── MODO AUTENTICADO ─────────────────────────────────────────
      await api.put(`/api/carrinho/${empresaId}/item/${itemId}`, { quantidade });
      await loadCart();
      return true;
    } catch (error: any) {
      console.error('Erro ao atualizar quantidade:', error);
      toast.error('Erro ao atualizar quantidade', { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuario, token, empresaId, loadCart]);

  // Remover item do carrinho
  const removeItem = useCallback(async (itemId: number) => {
    setIsUpdating(true);
    try {
      // ── MODO VISITANTE ──────────────────────────────────────────
      if (!usuario || !token) {
        const itensAtuais = loadGuestCartFromStorage(empresaId);
        const novosItens = itensAtuais.filter(it => it.id !== itemId);

        saveGuestCartToStorage(empresaId, novosItens);
        setCartData(prev => ({
          ...prev,
          itens: novosItens,
          total: calculateGuestTotal(novosItens)
        }));
        toast.success('Item removido do carrinho', { duration: 1500 });
        return true;
      }

      // ── MODO AUTENTICADO ─────────────────────────────────────────
      await api.delete(`/api/carrinho/${empresaId}/item/${itemId}`);
      toast.success('Item removido do carrinho', { duration: 1500 });
      await loadCart();
      return true;
    } catch (error: any) {
      console.error('Erro ao remover item:', error);
      toast.error('Erro ao remover item', { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuarioId, token, empresaId, loadCart]);

  // Limpar carrinho
  const clearCart = useCallback(async () => {
    setIsUpdating(true);
    try {
      // Limpar storage local do visitante sempre
      localStorage.removeItem(getGuestStorageKey(empresaId));

      // Limpar estado local
      setCartData({
        carrinho: null,
        itens: [],
        total: 0,
        endereco_entrega: null,
        entregaGratis: false
      });

      // ── MODO AUTENTICADO ─────────────────────────────────────────
      if (usuarioId && token) {
        await api.delete(`/api/carrinho/${empresaId}/limpar`);
      }

      toast.success('Carrinho limpo', { duration: 1500 });
      return true;
    } catch (error: any) {
      toast.error('Erro ao limpar carrinho', { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuarioId, token, empresaId]);

  // Atualizar item completo (quantidade, observações, personalizações)
  const updateItem = useCallback(async (
    itemId: number,
    quantidade: number,
    observacoes: string,
    personalizacoes: any[]
  ) => {
    setIsUpdating(true);
    try {
      // ── MODO VISITANTE ──────────────────────────────────────────
      if (!usuarioId || !token) {
        const itensAtuais = loadGuestCartFromStorage(empresaId);
        const novosItens = itensAtuais.map(it => {
          if (it.id === itemId) {
            return {
              ...it,
              quantidade,
              observacoes,
              personalizacoes
            };
          }
          return it;
        });

        saveGuestCartToStorage(empresaId, novosItens);
        setCartData(prev => ({
          ...prev,
          itens: novosItens,
          total: calculateGuestTotal(novosItens)
        }));
        return true;
      }

      // ── MODO AUTENTICADO ─────────────────────────────────────────
      const payload = {
        quantidade,
        observacoes,
        personalizacoes
      };

      await api.put(`/api/carrinho/${empresaId}/item/${itemId}`, payload);
      await loadCart();
      return true;
    } catch (error: any) {
      console.error('Erro ao atualizar item:', error);
      toast.error('Erro ao atualizar item', { duration: 2000 });
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuarioId, token, empresaId, loadCart]);

  // Atualizar observações do carrinho
  const updateCartObservations = useCallback(async (observacoes: string) => {
    if (!usuarioId || !token) {
      return true;
    }

    setIsUpdating(true);
    try {
      await api.put(`/api/carrinho/${empresaId}/observacoes`, { observacoes });
      await loadCart();
      return true;
    } catch (error: any) {
      console.error('Erro ao atualizar observações:', error);
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [usuarioId, token, empresaId, loadCart]);

  // Sincronizar itens de visitante assim que o login for efetuado
  useEffect(() => {
    const authKey = `${usuarioId || 'guest'}_${token ? 'auth' : 'noauth'}_${empresaId}`;
    if (lastSyncedAuthRef.current === authKey) {
      return;
    }
    lastSyncedAuthRef.current = authKey;

    const handleAuthChange = async () => {
      if (usuarioId && token && !sincronizandoRef.current) {
        const guestItems = loadGuestCartFromStorage(empresaId);

        if (guestItems.length > 0) {
          sincronizandoRef.current = true;
          try {
            console.log('🔄 Sincronizando carrinho de visitante com a conta do usuário...');
            await api.post(`/api/carrinho/${empresaId}/sincronizar`, { itens: guestItems });
            localStorage.removeItem(getGuestStorageKey(empresaId));
            console.log('✅ Carrinho de visitante sincronizado com sucesso!');
          } catch (err) {
            console.error('❌ Falha ao sincronizar carrinho de visitante:', err);
          } finally {
            sincronizandoRef.current = false;
          }
        }

        // Carregar carrinho oficial e verificar benefícios
        await loadCart();
        verificarEntregaGratis();
      } else if (!usuarioId || !token) {
        // Se deslogou ou visitante, carregar o carrinho local de visitante
        const guestItems = loadGuestCartFromStorage(empresaId);
        setCartData({
          carrinho: null,
          itens: guestItems,
          total: calculateGuestTotal(guestItems),
          endereco_entrega: null,
          entregaGratis: false
        });
      }
    };

    handleAuthChange();
  }, [usuarioId, token, empresaId, loadCart, verificarEntregaGratis]);

  return {
    cartData,
    isLoading,
    isUpdating,
    loadCart,
    addToCart,
    updateItemQuantity,
    removeItem,
    clearCart,
    updateCartObservations,
    updateItem,
    verificarEntregaGratis,
    formatarEndereco,
    itemCount: cartData.itens.reduce((sum, item) => sum + item.quantidade, 0),
    hasItems: cartData.itens.length > 0
  };
};
